import React, { useState, useMemo } from "react";
import {
  useDepartmentsQuery,
  useDeleteDepartmentMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  Building03FreeIcons,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useClientSearch } from "@/utils/hooks/useClientSearch";

const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={Building03FreeIcons}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_DEPARTMENTS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_DEPARTMENTS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={Building03FreeIcons}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_DEPARTMENT")}
        </Link>
      )}
    </div>
  );
};

export const Departments = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    departmentId: null,
  });
  const location = useLocation();
  const basePath = location.pathname.includes("seasonal_hr")
    ? "/app/seasonal_hr/basic-data/departments"
    : "/app/makkah/basic-data/departments";

  const { data, isLoading, isFetching } = useDepartmentsQuery({
    page: currentPage,
    per_page: 10,
  });
  const departments = data?.data?.data ?? [];
  const meta = data?.data?.meta ?? data?.meta ?? {};
  const { filteredData, ...restClientSearch } = useClientSearch(departments);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const loading = isLoading || isFetching;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const pagination =
    meta?.total != null && meta.total > 0
      ? {
          current: meta?.current_page ?? currentPage,
          total: meta.total,
          pageSize: meta?.per_page ?? 10,
          onChange: handlePageChange,
        }
      : undefined;

  const { mutate: deleteDepartment, isPending: isDeleting } =
    useDeleteDepartmentMutation();

  const handleDeleteClick = (departmentId) => {
    setDeleteModalState({ isOpen: true, departmentId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, departmentId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.departmentId) {
      deleteDepartment(deleteModalState.departmentId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, departmentId: null });
          queryClient.invalidateQueries({ queryKey: ["departments"] });
        },
      });
    }
  };

  const columns = useMemo(
    () => [
      { name: t("NAME_AR"), key: "name_ar", filterable: true },
      { name: t("NAME_EN"), key: "name_en", filterable: true },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  const getActions = (rowData) => [
    {
      key: "edit",
      label: (
        <Link
          to={`${basePath}/${rowData.id}/edit`}
          className="d-flex align-items-center gap-2"
        >
          <HugeiconsIcon size={16} icon={Edit02Icon} />
          {t("EDIT")}
        </Link>
      ),
    },
    { type: "divider" },
    {
      key: "delete",
      label: (
        <div className="d-flex align-items-center gap-2">
          <HugeiconsIcon size={16} icon={DeleteIcon} />
          {t("DELETE")}
        </div>
      ),
      onClick: () => handleDeleteClick(rowData.id),
      danger: true,
    },
  ];

  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_DEPARTMENT")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={filteredData}
        {...restClientSearch}
        columns={columns}
        loading={loading}
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
        pagination={pagination}
      />

      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_DEPARTMENT"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};
